import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

// A free-text answer that was typed and then cleared still has a key in
// `answers`, so blank strings must count as unanswered.
const isAnswered = (answer) =>
  answer !== undefined && !(typeof answer === 'string' && answer.trim() === '');

const ExamPage = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [examData, setExamData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // True once saved answers have been restored from the server (or that failed).
  const [ready, setReady] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error

  const deadlineRef = useRef(null);      // Date.now() value at which time runs out
  const submittingRef = useRef(false);
  const autoSubmittedRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const dirtyRef = useRef({});           // question id -> value not yet saved (null = cleared)
  const flushingRef = useRef(false);
  const stopSavingRef = useRef(false);
  const retryTimerRef = useRef(null);
  const textTimerRef = useRef(null);

  // Disable copy/paste/right-click on exam content
  useEffect(() => {
    const preventCopy = (e) => e.preventDefault();
    const preventContextMenu = (e) => e.preventDefault();
    const preventKeyboard = (e) => {
      // Prevent Ctrl+C, Ctrl+A, Ctrl+X, Ctrl+P
      if (e.ctrlKey && (e.key === 'c' || e.key === 'a' || e.key === 'x' || e.key === 'p')) {
        e.preventDefault();
      }
    };

    document.addEventListener('copy', preventCopy);
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventKeyboard);

    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventKeyboard);
    };
  }, []);

  // The result page is public, so it is addressed by this attempt's own
  // token as well as its id -- the id alone is guessable.
  const goToResult = useCallback((token) => {
    sessionStorage.removeItem('examData');
    navigate(`/exam/result/${attemptId}?t=${encodeURIComponent(token || '')}`);
  }, [attemptId, navigate]);

  useEffect(() => {
    const stored = sessionStorage.getItem('examData');
    if (!stored) {
      navigate('/exam');
      return;
    }
    const data = JSON.parse(stored);
    if (data.attempt_id !== parseInt(attemptId)) {
      navigate('/exam');
      return;
    }

    // Deadline from the start time, used if the server cannot be reached below.
    deadlineRef.current = new Date(data.started_at).getTime() + data.quiz.time_limit_minutes * 60 * 1000;
    setTimeLeft(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
    setExamData(data);

    // Restore answers saved before a reload, and take the time remaining from the
    // server so a wrong clock on this device cannot shorten or extend the exam.
    let cancelled = false;
    api.get(`/exam/attempt/${attemptId}/state`, { params: { t: data.session_token }, timeout: 8000 })
      .then(({ data: state }) => {
        if (cancelled) return;
        if (state.already_closed) {
          goToResult(data.session_token);
          return;
        }
        deadlineRef.current = Date.now() + state.seconds_remaining * 1000;
        setAnswers(prev => ({ ...(state.answers || {}), ...prev }));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => { cancelled = true; };
  }, [attemptId, navigate, goToResult]);

  // Timer. Browsers slow timers in background tabs and pause them while the
  // computer sleeps, so the time left is recomputed from the clock on every
  // tick rather than counted down.
  useEffect(() => {
    if (!examData || !ready) return;

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0 && !autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        handleSubmitRef.current(true);
      }
    };

    tick();
    const timer = setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [examData, ready]);

  // Send unsaved answers to the server one at a time, in order. On a network
  // failure the answers stay queued and are retried.
  const flushSaves = useCallback(async () => {
    if (flushingRef.current || stopSavingRef.current || !examData) return;
    flushingRef.current = true;
    clearTimeout(retryTimerRef.current);

    try {
      let entry;
      while (!stopSavingRef.current && (entry = Object.entries(dirtyRef.current)[0])) {
        const [questionId, value] = entry;
        setSaveStatus('saving');
        try {
          await api.post('/exam/answer', {
            attempt_id: parseInt(attemptId),
            session_token: examData.session_token,
            question_id: parseInt(questionId),
            value
          });
          // Keep it queued if it changed while this request was in flight.
          if (dirtyRef.current[questionId] === value) delete dirtyRef.current[questionId];
        } catch (error) {
          const data = error.response?.data;
          if (data?.already_closed) {
            stopSavingRef.current = true;
            goToResult(examData.session_token);
            return;
          }
          if (data?.time_up) {
            // The timer's submit takes it from here.
            stopSavingRef.current = true;
            return;
          }
          if (error.response?.status === 400) {
            delete dirtyRef.current[questionId];
            continue;
          }
          setSaveStatus('error');
          if (!error.response || error.response.status >= 500) {
            retryTimerRef.current = setTimeout(() => flushSaves(), 5000);
          }
          return;
        }
      }
      if (!stopSavingRef.current) setSaveStatus('saved');
    } finally {
      flushingRef.current = false;
    }
  }, [examData, attemptId, goToResult]);

  // Save typed text straight away when moving to another question.
  useEffect(() => {
    flushSaves();
  }, [currentQuestion, flushSaves]);

  useEffect(() => () => {
    clearTimeout(retryTimerRef.current);
    clearTimeout(textTimerRef.current);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId, answer) => {
    // If clicking the same answer, unselect it
    const next = answers[questionId] === answer ? undefined : answer;
    setAnswers(prev => {
      const newAnswers = { ...prev };
      if (next === undefined) {
        delete newAnswers[questionId];
      } else {
        newAnswers[questionId] = next;
      }
      return newAnswers;
    });

    dirtyRef.current[questionId] = next === undefined ? null : next;
    clearTimeout(textTimerRef.current);
    if (typeof next === 'string') {
      // Typing: save once the candidate pauses.
      textTimerRef.current = setTimeout(flushSaves, 1500);
    } else {
      flushSaves();
    }
  };

  const handleSubmit = useCallback(async (autoSubmit = false) => {
    if (submittingRef.current) return;

    if (!autoSubmit) {
      const unanswered = examData.questions
        .map((q, i) => (isAnswered(answers[q.id]) ? null : i + 1))
        .filter(n => n !== null);

      let message = 'Are you sure you want to submit the exam? You cannot change your answers after submission.';
      if (unanswered.length > 0) {
        const shown = unanswered.slice(0, 10).map(n => `Q${n}`).join(', ');
        const more = unanswered.length > 10 ? ` and ${unanswered.length - 10} more` : '';
        message = `You have ${unanswered.length} unanswered question${unanswered.length === 1 ? '' : 's'}: ${shown}${more}.\n\n` +
          'Unanswered questions will score 0. Submit anyway? You cannot change your answers after submission.';
      }
      if (!window.confirm(message)) {
        return;
      }
    }

    submittingRef.current = true;
    // The submit below carries every answer, so pending autosaves are dropped.
    stopSavingRef.current = true;
    clearTimeout(textTimerRef.current);
    clearTimeout(retryTimerRef.current);
    setSubmitting(true);

    try {
      const formattedAnswers = examData.questions.map(q => {
        const answer = answers[q.id];
        return {
          question_id: q.id,
          selected_option_id: q.question_type === 'multiple_choice' ? answer : null,
          true_false_answer: q.question_type === 'true_false' ? answer : null,
          free_text_answer: q.question_type === 'free_text' ? answer : null
        };
      });

      await api.post('/exam/submit', {
        attempt_id: parseInt(attemptId),
        session_token: examData.session_token,
        answers: formattedAnswers,
        auto: autoSubmit
      });

      goToResult(examData.session_token);
    } catch (error) {
      // Closed already, e.g. by the server after time ran out while offline.
      if (error.response?.data?.already_closed) {
        goToResult(examData.session_token);
        return;
      }

      submittingRef.current = false;
      stopSavingRef.current = false;

      if (autoSubmit && !error.response) {
        // Offline when time ran out: keep trying. Answers already saved count
        // even if this never gets through -- the server closes the attempt.
        setSaveStatus('error');
        setTimeout(() => handleSubmitRef.current(true), 10000);
        return;
      }

      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to submit exam. Please try again.';
      alert(errorMsg);
      // If session expired, redirect to exam page
      if (error.response?.status === 403) {
        sessionStorage.removeItem('examData');
        navigate('/exam');
      }
    } finally {
      setSubmitting(false);
    }
  }, [examData, answers, attemptId, navigate, goToResult]);
  handleSubmitRef.current = handleSubmit;

  if (!examData) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  const question = examData.questions[currentQuestion];
  const answeredCount = examData.questions.filter(q => isAnswered(answers[q.id])).length;
  const progress = (answeredCount / examData.questions.length) * 100;

  // Style to prevent text selection
  const noSelectStyle = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none'
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column', ...noSelectStyle }}>
      {/* Header - Compact */}
      <header style={{
        background: 'white',
        padding: '10px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        flexShrink: 0
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }} className="flex flex-between flex-center">
          <h2 style={{ fontSize: '16px', margin: 0 }}>{examData.quiz.title}</h2>
          <div className="flex gap-3 flex-center">
            {saveStatus !== 'idle' && (
              <div className="text-sm" style={{ color: saveStatus === 'error' ? '#ef4444' : '#6b7280' }}>
                {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Not saved, retrying'}
              </div>
            )}
            <div className="text-sm text-muted">
              {answeredCount}/{examData.questions.length}
            </div>
            <div style={{
              background: timeLeft < 300 ? '#fef2f2' : '#f3f4f6',
              padding: '6px 12px',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '16px',
              color: timeLeft < 300 ? '#ef4444' : '#1f2937'
            }}>
              {formatTime(timeLeft)}
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{
          height: '3px',
          background: '#e5e7eb',
          marginTop: '10px',
          borderRadius: '2px'
        }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: '#f97316',
            borderRadius: '2px',
            transition: 'width 0.3s'
          }} />
        </div>
      </header>

      {/* Main Content - Flexible height */}
      <main style={{
        maxWidth: '1100px',
        width: '100%',
        margin: '0 auto',
        padding: '16px 20px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        boxSizing: 'border-box'
      }}>
        {/* Question Card */}
        <div className="card" style={{ padding: '20px', flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
          {/* Question Header */}
          <div className="flex flex-between flex-center" style={{ marginBottom: '12px' }}>
            <span className="badge badge-info">
              Q{currentQuestion + 1}/{examData.questions.length}
            </span>
            <span className="badge badge-warning">{question.points} pt{question.points > 1 ? 's' : ''}</span>
          </div>

          {/* Question Text */}
          <h3 style={{ fontSize: '18px', marginBottom: '16px', lineHeight: '1.5' }}>
            {question.question_text}
          </h3>

          {/* Chart or diagram the question refers to. Many stems say "shown
              above", so this sits between the text and the options. */}
          {question.image_url && (
            <div style={{ marginBottom: '18px', textAlign: 'center' }}>
              <img
                src={question.image_url}
                alt="Figure for this question"
                style={{
                  maxWidth: '100%',
                  maxHeight: '380px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '6px',
                  background: 'white'
                }}
              />
            </div>
          )}

          {/* Answer Options */}
          <div style={{ flex: '1 1 auto' }}>
            {question.question_type === 'multiple_choice' && (
              <div>
                {question.options.map(option => (
                  <div
                    key={option.id}
                    onClick={() => handleAnswer(question.id, option.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 14px',
                      border: answers[question.id] === option.id ? '2px solid #f97316' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      background: answers[question.id] === option.id ? '#fff7ed' : 'white',
                      transition: 'all 0.2s',
                      fontSize: '15px'
                    }}
                  >
                    <span style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: answers[question.id] === option.id ? '5px solid #f97316' : '2px solid #d1d5db',
                      marginRight: '10px',
                      flexShrink: 0
                    }} />
                    {option.option_text}
                  </div>
                ))}
              </div>
            )}

            {question.question_type === 'true_false' && (
              <div className="flex gap-3">
                {[true, false].map(value => (
                  <div
                    key={value.toString()}
                    onClick={() => handleAnswer(question.id, value)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px',
                      border: answers[question.id] === value ? '2px solid #f97316' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: answers[question.id] === value ? '#fff7ed' : 'white',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: answers[question.id] === value ? '5px solid #f97316' : '2px solid #d1d5db',
                      marginRight: '8px',
                      flexShrink: 0
                    }} />
                    {value ? 'True' : 'False'}
                  </div>
                ))}
              </div>
            )}

            {question.question_type === 'free_text' && (
              <textarea
                value={answers[question.id] || ''}
                onChange={e => handleAnswer(question.id, e.target.value)}
                placeholder="Type your answer here..."
                rows="4"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '15px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            )}
          </div>

          {/* Navigation and Question Numbers - Combined */}
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            {/* Question Numbers */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              {examData.questions.map((_, index) => {
                const answered = isAnswered(answers[examData.questions[index].id]);
                const isCurrent = currentQuestion === index;
                return (
                  <button
                    key={index}
                    onClick={() => setCurrentQuestion(index)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: isCurrent ? '2px solid #10b981' : '1px solid #d1d5db',
                      background: answered
                        ? '#f97316'
                        : isCurrent
                        ? '#ecfdf5'
                        : 'white',
                      color: answered ? 'white' : '#1f2937',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.15s'
                    }}
                    title={answered ? `Question ${index + 1} (Answered)` : `Question ${index + 1}`}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>

            {/* Prev/Next/Submit Row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px'
            }}>
              <button
                onClick={() => setCurrentQuestion(prev => prev - 1)}
                disabled={currentQuestion === 0}
                style={{
                  minWidth: '90px',
                  padding: '10px 16px',
                  background: currentQuestion === 0 ? '#e5e7eb' : '#f97316',
                  color: currentQuestion === 0 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  opacity: currentQuestion === 0 ? 0.6 : 1,
                  cursor: currentQuestion === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Prev
              </button>

              <button
                onClick={() => handleSubmit(false)}
                disabled={submitting}
                style={{
                  padding: '10px 24px',
                  fontSize: '15px',
                  fontWeight: '600',
                  background: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {submitting ? 'Submitting...' : `Submit (${answeredCount}/${examData.questions.length})`}
              </button>

              <button
                onClick={() => setCurrentQuestion(prev => prev + 1)}
                disabled={currentQuestion === examData.questions.length - 1}
                style={{
                  minWidth: '90px',
                  padding: '10px 16px',
                  background: currentQuestion === examData.questions.length - 1 ? '#e5e7eb' : '#f97316',
                  color: currentQuestion === examData.questions.length - 1 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  opacity: currentQuestion === examData.questions.length - 1 ? 0.6 : 1,
                  cursor: currentQuestion === examData.questions.length - 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExamPage;
